// One place for everything about the signed-in session: reading the token,
// expiry, clearing it, and telling the UI it changed.
import {
  AUTH_TOKEN,
  USER_NAME,
  USER_ID,
  USER_PHONE,
  USER_ROLE,
  USER_STATUS,
  USER_GITHUB_USERNAME,
  USER_GITHUB_PROFILE,
} from '../configs/configs';

// Fired on window whenever the session starts or ends; the navbar listens for it.
export const SESSION_EVENT = 'pk:session-changed';

const SESSION_KEYS = [
  AUTH_TOKEN,
  USER_NAME,
  USER_ID,
  USER_PHONE,
  USER_ROLE,
  USER_STATUS,
  USER_GITHUB_USERNAME,
  USER_GITHUB_PROFILE,
];

// Routes that only make sense when signed in; a 401 there sends the user to /auth.
const PROTECTED_PREFIXES = ['/checkout', '/profile', '/admin'];

const safeStorage = {
  get: key => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  remove: key => {
    try {
      localStorage.removeItem(key);
    } catch {
      // Storage unavailable; nothing to clear.
    }
  },
};

export const decodeToken = token => {
  try {
    const [, payload] = String(token).split('.');
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return null;
  }
};

// Treat a token as expired a few seconds early so a request doesn't race the deadline.
export const isTokenExpired = token => {
  const payload = decodeToken(token);
  if (!payload) return true;
  if (!payload.exp) return false;
  return payload.exp * 1000 <= Date.now() + 5000;
};

export const notifySessionChanged = () => {
  try {
    window.dispatchEvent(new Event(SESSION_EVENT));
  } catch {
    // Non-browser environment.
  }
};

export const clearSession = () => {
  SESSION_KEYS.forEach(safeStorage.remove);
  notifySessionChanged();
};

// The token if it's present and still valid; an expired one is cleared on the spot.
export const getSessionToken = () => {
  const token = safeStorage.get(AUTH_TOKEN);
  if (!token) return null;
  if (token.split('.').length !== 3 || isTokenExpired(token)) {
    clearSession();
    return null;
  }
  return token;
};

export const hasSession = () => Boolean(getSessionToken());

export const isProtectedPath = pathname =>
  PROTECTED_PREFIXES.some(
    prefix => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );

// Only same-site relative paths are allowed as a post-login destination.
export const safeNextPath = next => {
  if (typeof next !== 'string') return null;
  if (!next.startsWith('/') || next.startsWith('//') || next.startsWith('/auth')) {
    return null;
  }
  return next;
};

export const loginPath = from => {
  const next = safeNextPath(from);
  return next ? `/auth?next=${encodeURIComponent(next)}` : '/auth';
};
